import {useMemo, useState} from "react";
import {Plus} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {Loader} from "@/src/modules/core/components";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import useResidentMembership from "@/src/modules/core/hooks/use-resident-membership";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {isAmenityKeyRestricted} from "@/src/modules/households/lib/household-amenity-restriction";
import {RESIDENT_SAFELISTING_RESTRICTION_MESSAGE} from "../lib/resident-amenity-restriction-copy";
import {ResidentAmenityRestrictionNotice} from "./resident-amenity-restriction-notice";
import {SafelistingDetailModal} from "@/src/modules/safelistings/components/safelisting-detail-modal";
import {SafelistingsSheetForm} from "@/src/modules/safelistings/components/sheet-form";
import {useResidentHomeSafelistingsAccountability} from "@/src/modules/safelistings/hooks/use-resident-home-safelistings-accountability";
import {useSafelistingsPaginated} from "@/src/modules/safelistings/hooks/use-safelistings-paginated";
import {getResidentHomeQuotaDisplay} from "@/src/modules/safelistings/lib/format-resident-home-safelistings-quota";
import {safelistingsService} from "@/src/modules/safelistings/services";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_BLOCK_TITLE_CLASS,
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
  RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
  RESIDENT_HOME_PILLAR_FILLER_CLASS,
  RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS,
  RESIDENT_HOME_PILLAR_LIST_CLASS,
  RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS,
  RESIDENT_HOME_PREVIEW_LIMIT,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";
import {useResidentHomeOwnerTenantGates} from "../hooks/use-hide-resident-home-tenant-data";
import {RESIDENT_HOME_SAFELISTINGS_ICON} from "../lib/resident-home-teaser";
import {ResidentHomeCardPreviewFooter} from "./resident-home-card-preview-footer";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";
import {ResidentHomeNoTenantData} from "./resident-home-no-tenant-data";
import {ResidentHomeSafelistingsPreviewRow} from "./resident-home-safelistings-preview-row";
import {ResidentHomeSafelistingsQuotaSummary} from "./resident-home-safelistings-quota-summary";
import {ResidentHomeSafelistingsSheet} from "./resident-home-safelistings-sheet";

const PREVIEW_LIMIT = RESIDENT_HOME_PREVIEW_LIMIT;

type ResidentHomeSafelistingsCardProps = {
  className?: string;
};

export function ResidentHomeSafelistingsCard({className}: ResidentHomeSafelistingsCardProps) {
  const userProperty = useStoredUserProperty();
  const householdId = useResidentHouseholdId();
  const membership = useResidentMembership();
  const {hideTenantData, hideSafelistingCreate} = useResidentHomeOwnerTenantGates();
  const {household} = useHousehold(householdId, {enabled: !hideTenantData});

  const [sheetOpen, setSheetOpen] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const filter = useMemo(
    () =>
      userProperty?.id && householdId
        ? {context_property_id: userProperty.id, household_id: householdId}
        : {},
    [householdId, userProperty?.id],
  );

  const {safelistings, totalRecords, isPending} = useSafelistingsPaginated({
    rows: PREVIEW_LIMIT,
    page: 1,
    filter,
    sort: "-start_date",
    enabled: !hideTenantData && Boolean(userProperty?.id && householdId),
  });

  const {accountability, isPending: isQuotaPending} = useResidentHomeSafelistingsAccountability({
    householdId,
    propertyId: userProperty?.id ?? null,
    enabled: !hideTenantData && Boolean(householdId),
  });
  const quotaDisplay = useMemo(() => getResidentHomeQuotaDisplay(accountability), [accountability]);

  const canCreate = safelistingsService.getIsCreateEnabled();
  const isDetailEnabled = safelistingsService.getIsShowEnabled();
  const isAmenityBlocked = isAmenityKeyRestricted(household, "safelisting");
  const isQuotaExhausted = quotaDisplay?.isQuotaExhausted ?? false;
  const isCreateBlocked = isAmenityBlocked || isQuotaExhausted;
  const newButtonTitle = isQuotaExhausted
    ? "No safelistings left in this period"
    : isAmenityBlocked
      ? "Safelistings are restricted for this household"
      : undefined;
  const showExpand = totalRecords > PREVIEW_LIMIT;

  const openNew = () => setSheetOpen(true);

  return (
    <>
      <Card className={cn(RESIDENT_HOME_CARD_SHELL_CLASS, "flex h-full min-h-0 flex-col", className)}>
        <CardContent
          className={cn(
            RESIDENT_HOME_CARD_CONTENT_CLASS,
            RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
            "min-h-0 flex-1",
          )}
        >
          {!hideTenantData && isAmenityBlocked ? (
            <ResidentAmenityRestrictionNotice message={RESIDENT_SAFELISTING_RESTRICTION_MESSAGE} />
          ) : null}

          <div className={RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS}>
            <ResidentHomeIconTextBlock
              icon={RESIDENT_HOME_SAFELISTINGS_ICON.icon}
              iconWrapperClassName={RESIDENT_HOME_SAFELISTINGS_ICON.iconWrapperClassName}
              iconClassName={RESIDENT_HOME_SAFELISTINGS_ICON.iconClassName}
              className="min-w-0 flex-1 items-start"
              title={<p className={RESIDENT_HOME_BLOCK_TITLE_CLASS}>Safelistings</p>}
              subtitle={
                !hideTenantData && householdId ? (
                  <ResidentHomeSafelistingsQuotaSummary
                    accountability={accountability}
                    isPending={isQuotaPending}
                  />
                ) : undefined
              }
            />
            {canCreate && !hideSafelistingCreate ? (
              <Button
                type="button"
                size="sm"
                className="shrink-0"
                disabled={isCreateBlocked}
                title={newButtonTitle}
                onClick={openNew}
              >
                <Plus className="size-4" aria-hidden />
                New
              </Button>
            ) : (
              <span className={RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS} aria-hidden>
                ·
              </span>
            )}
          </div>

          <div className={cn(RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS, RESIDENT_HOME_TEXT_INSET_CLASS)}>
            {hideTenantData ? (
              <ResidentHomeNoTenantData />
            ) : !householdId ? (
              <p className="text-muted-foreground text-sm">
                Link a household to manage safelistings.
              </p>
            ) : isPending ? (
              <Loader className="min-h-24" />
            ) : safelistings.length === 0 ? (
              <p className="text-muted-foreground text-sm">No safelistings yet.</p>
            ) : (
              <div className={RESIDENT_HOME_PILLAR_LIST_CLASS}>
                {safelistings.map((row) => (
                  <ResidentHomeSafelistingsPreviewRow
                    key={row.safelisting_id}
                    safelisting={row}
                    membership={membership}
                    amenityRestricted={isAmenityBlocked}
                    onSelect={(id) => isDetailEnabled && setDetailId(id)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={RESIDENT_HOME_PILLAR_FILLER_CLASS} aria-hidden />

          {!hideTenantData && householdId && !isPending && safelistings.length > 0 ? (
            <ResidentHomeCardPreviewFooter
              showingCount={safelistings.length}
              totalRecords={totalRecords}
              onShowMore={showExpand ? () => setExpandOpen(true) : undefined}
            />
          ) : (
            <div aria-hidden className={RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS} />
          )}
        </CardContent>
      </Card>

      {sheetOpen ? <SafelistingsSheetForm open onClose={() => setSheetOpen(false)} /> : null}

      <ResidentHomeSafelistingsSheet
        open={expandOpen}
        onOpenChange={setExpandOpen}
        filter={filter}
        householdId={householdId}
        accountability={accountability}
        isQuotaPending={isQuotaPending}
        isCreateBlocked={isCreateBlocked}
        isSafelistingAmenityBlocked={isAmenityBlocked}
        newButtonTitle={newButtonTitle}
        hideCreate={hideSafelistingCreate}
        onNew={openNew}
        onSelectDetail={(id) => setDetailId(id)}
      />

      <SafelistingDetailModal
        open={detailId != null}
        safelistingId={detailId}
        onOpenChange={(open) => !open && setDetailId(null)}
        showPrivateNotes={false}
      />
    </>
  );
}
